import { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { CustomImageExtension } from './editor/CustomImageExtension';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { Mark } from '@tiptap/core';
import { 
  Bold, Italic, Strikethrough, Heading2, Heading3, 
  List, ListOrdered, Quote, Image as ImageIcon, Link as LinkIcon, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo, Redo, RemoveFormatting,
  Palette, Type, ChevronDown
} from 'lucide-react';

// Custom FontSize extension (TipTap doesn't have an official one)
const FontSize = Mark.create({
  name: 'fontSize',
  addAttributes() {
    return {
      fontSize: {
        default: null,
        parseHTML: el => el.style.fontSize?.replace(/['"]+/g, ''),
        renderHTML: attrs => {
          if (!attrs.fontSize) return {};
          return { style: `font-size: ${attrs.fontSize}` };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span', getAttrs: el => el.style.fontSize ? {} : false }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => {
        return chain().setMark(this.name, { fontSize }).run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().unsetMark(this.name).run();
      },
    };
  },
});

const FONT_SIZES = [
  { label: '12px', value: '12px' },
  { label: '14px', value: '14px' },
  { label: '16px (عادي)', value: '16px' },
  { label: '18px', value: '18px' },
  { label: '20px', value: '20px' },
  { label: '24px', value: '24px' },
  { label: '28px', value: '28px' },
  { label: '32px', value: '32px' },
  { label: '36px', value: '36px' },
];

const FONT_FAMILIES = [
  { label: 'افتراضي', value: '' },
  { label: 'Cairo', value: 'Cairo' },
  { label: 'Tajawal', value: 'Tajawal' },
  { label: 'Amiri', value: 'Amiri' },
  { label: 'Noto Sans Arabic', value: 'Noto Sans Arabic' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Roboto', value: 'Roboto' },
];

const COLORS = [
  '#000000', '#374151', '#6B7280', '#9CA3AF',
  '#EF4444', '#DC2626', '#B91C1C',
  '#F59E0B', '#D97706', '#B45309',
  '#22C55E', '#16A34A', '#15803D',
  '#3B82F6', '#2563EB', '#1D4ED8',
  '#8B5CF6', '#7C3AED', '#6D28D9',
  '#EC4899', '#DB2777', '#BE185D',
  '#FFFFFF',
];

const DropdownMenu = ({ label, icon: Icon, children, isOpen, onToggle }) => {
  return (
    <div className="toolbar-dropdown-wrap">
      <button
        type="button"
        className={`toolbar-btn toolbar-dropdown-btn ${isOpen ? 'is-active' : ''}`}
        onClick={onToggle}
        title={label}
      >
        {Icon && <Icon size={18} />}
        <ChevronDown size={12} />
      </button>
      {isOpen && (
        <div className="toolbar-dropdown-menu">
          {children}
        </div>
      )}
    </div>
  );
};

const MenuBar = ({ editor }) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  if (!editor) {
    return null;
  }

  const toggleDropdown = (name) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };

  const closeDropdowns = () => setOpenDropdown(null);

  const addImage = () => {
    const url = window.prompt('أدخل رابط الصورة (URL):');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('أدخل الرابط:', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="tiptap-toolbar" onClick={(e) => { if (e.target === e.currentTarget) closeDropdowns(); }}>
      {/* Headings */}
      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
          title="عنوان رئيسي (H2)"
        >
          <Heading2 size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`}
          title="عنوان فرعي (H3)"
        >
          <Heading3 size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      {/* Font Family */}
      <div className="toolbar-group">
        <DropdownMenu label="نوع الخط" icon={Type} isOpen={openDropdown === 'font'} onToggle={() => toggleDropdown('font')}>
          {FONT_FAMILIES.map(f => (
            <button
              key={f.value}
              type="button"
              className={`dropdown-item ${editor.isActive('textStyle', { fontFamily: f.value }) ? 'active' : ''}`}
              style={{ fontFamily: f.value || 'inherit' }}
              onClick={() => {
                if (f.value) {
                  editor.chain().focus().setFontFamily(f.value).run();
                } else {
                  editor.chain().focus().unsetFontFamily().run();
                }
                closeDropdowns();
              }}
            >
              {f.label}
            </button>
          ))}
        </DropdownMenu>

        {/* Font Size */}
        <DropdownMenu label="حجم الخط" icon={null} isOpen={openDropdown === 'size'} onToggle={() => toggleDropdown('size')}>
          {FONT_SIZES.map(s => (
            <button
              key={s.value}
              type="button"
              className={`dropdown-item ${editor.isActive('fontSize', { fontSize: s.value }) ? 'active' : ''}`}
              onClick={() => {
                editor.chain().focus().setFontSize(s.value).run();
                closeDropdowns();
              }}
            >
              {s.label}
            </button>
          ))}
        </DropdownMenu>
      </div>

      <div className="toolbar-divider"></div>

      {/* Bold, Italic, Strike, ClearFormat */}
      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`toolbar-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
          title="عريض"
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`toolbar-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
          title="مائل"
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`toolbar-btn ${editor.isActive('strike') ? 'is-active' : ''}`}
          title="خط يتوسطه النص"
        >
          <Strikethrough size={18} />
        </button>

        {/* Text Color */}
        <DropdownMenu label="لون النص" icon={Palette} isOpen={openDropdown === 'color'} onToggle={() => toggleDropdown('color')}>
          <div className="color-grid">
            {COLORS.map(c => (
              <button
                key={c}
                type="button"
                className={`color-swatch ${editor.isActive('textStyle', { color: c }) ? 'active' : ''}`}
                style={{ backgroundColor: c, border: c === '#FFFFFF' ? '1px solid #ccc' : 'none' }}
                onClick={() => {
                  editor.chain().focus().setColor(c).run();
                  closeDropdowns();
                }}
                title={c}
              />
            ))}
          </div>
          <button
            type="button"
            className="dropdown-item unset-color-btn"
            onClick={() => {
              editor.chain().focus().unsetColor().run();
              closeDropdowns();
            }}
          >
            إزالة اللون
          </button>
        </DropdownMenu>

        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          className="toolbar-btn"
          title="مسح التنسيق"
        >
          <RemoveFormatting size={18} />
        </button>
      </div>

      {/* Alignment */}
      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
          title="محاذاة لليمين"
        >
          <AlignRight size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
          title="توسيط"
        >
          <AlignCenter size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
          title="محاذاة لليسار"
        >
          <AlignLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={`toolbar-btn ${editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}`}
          title="ضبط (Justify)"
        >
          <AlignJustify size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      {/* Lists, Quote */}
      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`toolbar-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
          title="قائمة نقطية"
        >
          <List size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`toolbar-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
          title="قائمة رقمية"
        >
          <ListOrdered size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`toolbar-btn ${editor.isActive('blockquote') ? 'is-active' : ''}`}
          title="اقتباس"
        >
          <Quote size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      {/* Link, Image */}
      <div className="toolbar-group">
        <button
          type="button"
          onClick={setLink}
          className={`toolbar-btn ${editor.isActive('link') ? 'is-active' : ''}`}
          title="إدراج رابط"
        >
          <LinkIcon size={18} />
        </button>
        <button
          type="button"
          onClick={addImage}
          className="toolbar-btn"
          title="إدراج صورة"
        >
          <ImageIcon size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      {/* Undo / Redo */}
      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="toolbar-btn"
          title="تراجع"
        >
          <Undo size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="toolbar-btn"
          title="إعادة التراجع"
        >
          <Redo size={18} />
        </button>
      </div>
    </div>
  );
};

const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'right',
      }),
      CustomImageExtension.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'tiptap-prose',
        dir: 'rtl',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when value prop changes externally (e.g., when editing an existing post)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <div className="tiptap-container">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="tiptap-editor-content" />
    </div>
  );
};

export default RichTextEditor;
